import React, { useRef, useEffect, useState } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { apiFetch } from '../../lib/api';
import './Map.css';

const MapPage = () => {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const [nodes, setNodes] = useState([]);
  const [filter, setFilter] = useState('All');
  const [popupOpen, setPopupOpen] = useState(false);

  const getAlertMeta = (level) => {
    if (level == null || level <= 1) {
      return {
        color: '#4CAF50',
        severityText: 'Healthy',
        statusClass: 'marker-healthy',
        riskText: 'Low',
        riskClass: 'text-healthy',
      };
    }

    if (level <= 3) {
      return {
        color: '#EA6C52',
        severityText: 'Warning',
        statusClass: 'marker-warning',
        riskText: 'Medium',
        riskClass: 'text-warning',
      };
    }

    return {
      color: '#D04545',
      severityText: 'Critical',
      statusClass: 'marker-critical',
      riskText: 'High',
      riskClass: 'text-critical',
    };
  };

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatSync = (d) => {
    if (!d) return 'Never synced';
    const mins = Math.round((Date.now() - new Date(d).getTime()) / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    return `${Math.round(mins / 60)}h ago`;
  };

  const formatNumber = (value, suffix = '') => (
    value != null ? `${value}${suffix}` : '—'
  );

  const formatAnomaly = (value) => {
    if (value == null) return '—';
    return `${value > 0 ? '+' : ''}${value}°C`;
  };

  // Fetch nodes from Supabase
  useEffect(() => {
    const fetchNodes = async () => {
      try {
        const data = await apiFetch('/api/nodes/');
        setNodes(data);
      } catch (error) {
        console.error('Error fetching nodes:', error);
      }
    };
    fetchNodes();
  }, []);

  // Initialize the Map
  useEffect(() => {
    if (map.current) return;

    map.current = new maplibregl.Map({
      container: mapContainer.current,
      style: {
        version: 8,
        sources: {
          'esri-satellite': {
            type: 'raster',
            tiles: ['https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'],
            tileSize: 256,
          },
        },
        layers: [
          {
            id: 'satellite',
            type: 'raster',
            source: 'esri-satellite',
            paint: {
              'raster-brightness-max': 0.6,
              'raster-saturation': 0.3,
            },
          },
        ],
      },
      center: [34.9, 27.9],
      zoom: 7,
    });

    map.current.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'bottom-right');
  }, []);

  // Add and filter markers
  useEffect(() => {
    if (!map.current) return;

    document.querySelectorAll('.custom-marker').forEach((m) => m.remove());

    nodes.forEach((node) => {
      const lat = node.latitude;
      const lng = node.longitude;

      if (lat == null || lng == null) return;

      const {
        color,
        severityText,
        statusClass,
        riskText,
        riskClass,
      } = getAlertMeta(node.crw_alert_level);

      if (filter === 'Healthy' && severityText !== 'Healthy') return;
      if (filter === 'Warning' && severityText !== 'Warning') return;
      if (filter === 'Critical' && severityText !== 'Critical') return;

      const el = document.createElement('div');
      el.className = `custom-marker ${statusClass}`;
      el.innerHTML = `
        <div class="marker-dot"></div>
        <span class="marker-label">${node.name || 'Unnamed Node'}</span>
      `;

      const popupHTML = `
        <div class="popup-header">
          <h3><span class="popup-dot" style="color:${color}">●</span> ${node.name || 'Unnamed Node'}</h3>
          <p>${node.region || '—'} · <span style="color:${color}">${severityText}</span></p>
        </div>

        <div class="popup-section-label">NOAA SATELLITE DATA</div>
        <div class="popup-grid">
          <div class="popup-card"><span>Sea Surface Temperature</span><strong>${formatNumber(node.crw_sst, '°C')}</strong></div>
          <div class="popup-card"><span>Anomaly</span><strong class="${node.crw_sst_anomaly > 0 ? 'text-warning' : 'text-healthy'}">${formatAnomaly(node.crw_sst_anomaly)}</strong></div>
          <div class="popup-card"><span>Stress Level</span><strong>${formatNumber(node.crw_dhw)}</strong></div>
          <div class="popup-card">
            <span>Bleaching Risk</span>
            <strong class="${riskClass}">${riskText}</strong>
          </div>
        </div>

        <div class="popup-section-label">NODE DETAILS</div>
        <div class="popup-detail-grid">
          <div><span>Latitude</span><strong>${lat}</strong></div>
          <div><span>Longitude</span><strong>${lng}</strong></div>
          <div><span>Created By</span><strong>${node.created_by_name || 'System Admin'}</strong></div>
          <div><span>Status</span><strong>${node.is_active !== false ? 'Active' : 'Deactivated'}</strong></div>
        </div>

        <div class="popup-footer">
          <div class="popup-footer-row">
            <span>NOAA SYNCED</span>
            <span>${formatSync(node.crw_last_synced)}</span>
          </div>
          <div class="popup-footer-row">
            <span>CREATED</span>
            <span>${formatDate(node.created_at)}</span>
          </div>
          <div class="popup-footer-zone">${node.id || '—'}</div>
        </div>
      `;
      
      const popup = new maplibregl.Popup({
        offset: 28,
        maxWidth: '340px',
        focusAfterOpen: false,
      })
        .setHTML(popupHTML)
        .on('open', () => setPopupOpen(true))
        .on('close', () => setPopupOpen(false));

      new maplibregl.Marker({ element: el, anchor: 'center' })
        .setLngLat([lng, lat])
        .setPopup(popup)
        .addTo(map.current);
    });
  }, [nodes, filter]);

  return (
    <div className="map-page-wrapper">
      {/* Map canvas */}
      <div ref={mapContainer} className="map-canvas" />

      {/* Top overlay: title + filters */}
      <div className="map-top-overlay">
        <div className="map-title">
          <h1>Reef Node Map</h1>
          <p>Real-time monitoring of {nodes.length} coral reef nodes across the Red Sea</p>
        </div>

        <div className="map-filters">
          {['All', 'Healthy', 'Warning', 'Critical'].map((f) => (
            <button
              key={f}
              className={`filter-btn ${filter === f ? 'active' : ''}`}
              onClick={() => setFilter(f)}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {/* Bottom-left legend */}
      <div className={`map-legend ${popupOpen ? 'legend-muted' : ''}`}>
        <h4>Legend</h4>
        <div className="legend-item">
          <span className="legend-dot dot-healthy">●</span> Healthy (90–100%)
        </div>
        <div className="legend-item">
          <span className="legend-dot dot-warning">●</span> Warning (50–89%)
        </div>
        <div className="legend-item">
          <span className="legend-dot dot-critical">●</span> Critical (&lt;50%)
        </div>
      </div>
    </div>
  );
};

export default MapPage;
